import { useMe } from './queries.js';

/** Locale/timezone-aware formatting. Timestamps are stored in UTC and shown in the person's IANA zone. */
export function makeFormat(timeZone: string, locale: string) {
  const safe = (tz: string) => {
    try {
      new Intl.DateTimeFormat(locale, { timeZone: tz });
      return tz;
    } catch {
      return 'UTC';
    }
  };
  const tz = safe(timeZone);
  const day = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
  });
  const rel = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const dayKey = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return {
    timeZone: tz,
    today: (d: Date = new Date()) => day.format(d),
    date: (iso: string | Date) => date.format(new Date(iso)),
    time: (iso: string | Date) => time.format(new Date(iso)),
    relative(iso: string | Date, now: Date = new Date()) {
      const d = new Date(iso);
      const diff = (d.getTime() - now.getTime()) / 1000;
      const abs = Math.abs(diff);
      if (abs < 60) return rel.format(Math.round(diff), 'second');
      if (abs < 3600) return rel.format(Math.round(diff / 60), 'minute');
      const sameDay = dayKey.format(d) === dayKey.format(now);
      if (sameDay) return `today, ${time.format(d)}`;
      if (abs < 7 * 86400) return rel.format(Math.round(diff / 86400), 'day');
      return date.format(d);
    },
    number: (n: number) => new Intl.NumberFormat(locale).format(n),
    bytes: (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`),
  };
}

export function useFormat() {
  const me = useMe();
  return makeFormat(me.data?.workspace.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, me.data?.workspace.locale ?? 'en');
}
